import { normalizeSocial } from './validation/social';
import { socialLinksToArray } from './social/utils/socialLinks';

export type SocialPlatform = 'tiktok' | 'instagram' | 'youtube';

export type SocialAccount = {
  platform: SocialPlatform;
  handle: string | null;
  url: string;
  isPrimary: boolean;
  source: 'onboarding' | 'user';
  createdAt?: any;
  updatedAt?: any;
};

export const normalizePlatform = (value: string): SocialPlatform | null => {
  if (!value) return null;
  const normalized = value.trim().toLowerCase();
  if (normalized.includes('tiktok')) return 'tiktok';
  if (normalized.includes('instagram')) return 'instagram';
  if (normalized.includes('youtube')) return 'youtube';
  return null;
};

export const normalizeSocialInput = (
  platform: SocialPlatform,
  input: string
) => {
  const normalized = normalizeSocial(platform, input);
  return {
    handle: normalized.handle,
    url: normalized.url || '',
  };
};

export const getCanonicalSocials = (userDoc: any): SocialAccount[] => {
  if (userDoc?.socials && Array.isArray(userDoc.socials)) {
    return userDoc.socials.filter(Boolean);
  }

  const socials: SocialAccount[] = [];
  const legacyLinks = userDoc?.socialLinks || {};

  const addLegacy = (platformKey: string, value?: string) => {
    const platform = normalizePlatform(platformKey);
    if (!platform || !value) return;
    const normalized = normalizeSocial(platform, value);
    if (!normalized.url) return;
    socials.push({
      platform,
      handle: normalized.handle,
      url: normalized.url,
      isPrimary: false,
      source: 'user',
    });
  };

  addLegacy('tiktok', userDoc?.tiktokHandle || legacyLinks?.tiktok || userDoc?.tiktok);
  addLegacy('instagram', userDoc?.instagramHandle || legacyLinks?.instagram || userDoc?.instagram);
  addLegacy('youtube', userDoc?.youtubeHandle || legacyLinks?.youtube || userDoc?.youtube);

  if (userDoc?.primaryPlatform && userDoc?.primaryHandleOrUrl) {
    const platform = normalizePlatform(userDoc.primaryPlatform);
    if (platform) {
      const normalized = normalizeSocial(platform, userDoc.primaryHandleOrUrl);
      if (normalized.url) {
        socials.push({
          platform,
          handle: normalized.handle,
          url: normalized.url,
          isPrimary: true,
          source: 'user',
        });
      }
    }
  }

  return socials;
};

const DISPLAY_PLATFORMS: SocialPlatform[] = ['tiktok', 'instagram', 'youtube'];

/**
 * Ensure display-ready handle and url for the chip (same shape as modern profiles).
 * When handle is missing, derive it from url so the UI shows @handle instead of raw URL.
 */
function ensureCanonicalDisplay(
  platform: SocialPlatform,
  handle: string | null | undefined,
  url: string | null | undefined
): { handle: string | null; url: string } | null {
  if (!url || typeof url !== 'string' || !url.trim()) return null;
  const urlTrim = url.trim();
  const handleTrim = handle && typeof handle === 'string' ? handle.trim() : null;
  if (handleTrim) {
    const norm = normalizeSocial(platform, handleTrim);
    return { handle: norm.handle, url: norm.url || urlTrim };
  }
  const norm = normalizeSocial(platform, urlTrim);
  if (!norm.url) return null;
  return { handle: norm.handle, url: norm.url };
}

/**
 * Get socials for profile display with fallback: structured (socialAccounts, socialLinks array) then legacy (manual handles/URLs).
 * All entries are normalized to the same canonical shape (handle + url) so legacy chips render like modern ones (@handle, same pill style).
 * Does not mutate Firestore; read-only.
 */
export function getSocialsForProfileDisplay(profile: any, userDoc?: any): SocialAccount[] {
  const result: SocialAccount[] = [];
  const seen = new Set<SocialPlatform>();

  const getStructuredFromSocialAccounts = (p: any, platform: SocialPlatform): { handle: string | null; url: string } | null => {
    const acc = p?.socialAccounts?.[platform];
    if (!acc || typeof acc !== 'object') return null;
    const url = acc.profileUrl;
    const handle = acc.handle;
    if (url && typeof url === 'string' && url.trim()) {
      const canonical = ensureCanonicalDisplay(platform, handle && typeof handle === 'string' ? handle.trim() : null, url.trim());
      return canonical;
    }
    if (handle && typeof handle === 'string' && handle.trim()) {
      const norm = normalizeSocial(platform, handle.trim());
      if (norm.url) return { handle: norm.handle, url: norm.url };
    }
    return null;
  };

  const getStructuredFromLinksArray = (p: any, platform: SocialPlatform): { handle: string | null; url: string } | null => {
    const links = socialLinksToArray(p?.socialLinks, { dedupe: true });
    const link = links.find((l: any) => l.platform === platform);
    if (!link?.url) return null;
    const canonical = ensureCanonicalDisplay(platform, link.handle, link.url);
    return canonical;
  };

  const getLegacyValue = (obj: any, platform: SocialPlatform): string | null => {
    if (!obj || typeof obj !== 'object') return null;
    const key = platform === 'tiktok' ? 'tiktok' : platform === 'instagram' ? 'instagram' : 'youtube';
    const legacyLinks = obj.socialLinks && typeof obj.socialLinks === 'object' && !Array.isArray(obj.socialLinks) ? obj.socialLinks : {};
    const v =
      (platform === 'tiktok' && (obj.tiktokHandle || obj.tiktok)) ||
      (platform === 'instagram' && (obj.instagramHandle || obj.instagram)) ||
      (platform === 'youtube' && (obj.youtubeHandle || obj.youtube)) ||
      (typeof legacyLinks[key] === 'string' ? legacyLinks[key] : null);
    return v && String(v).trim() ? String(v).trim() : null;
  };

  const addFromLegacy = (platform: SocialPlatform, value: string) => {
    const platformNorm = normalizePlatform(platform);
    if (!platformNorm) return null;
    const normalized = normalizeSocial(platformNorm, value);
    if (!normalized.url) return null;
    if (typeof console !== 'undefined' && console.log && normalized.handle) {
      console.log('[profile-fallback] normalized legacy', platform, 'handle ->', '@' + normalized.handle);
    }
    return {
      platform: platformNorm,
      handle: normalized.handle,
      url: normalized.url,
      isPrimary: false,
      source: 'user' as const,
    };
  };

  for (const platform of DISPLAY_PLATFORMS) {
    if (seen.has(platform)) continue;

    const fromAccounts = getStructuredFromSocialAccounts(profile, platform);
    const fromArray = getStructuredFromLinksArray(profile, platform);
    const legacyProfileVal = getLegacyValue(profile, platform);
    const legacyUserVal = userDoc ? getLegacyValue(userDoc, platform) : null;

    let chosen: SocialAccount | null = null;

    if (fromAccounts?.url) {
      chosen = {
        platform,
        handle: fromAccounts.handle,
        url: fromAccounts.url,
        isPrimary: false,
        source: 'user',
      };
    } else if (fromArray?.url) {
      chosen = {
        platform,
        handle: fromArray.handle,
        url: fromArray.url,
        isPrimary: false,
        source: 'user',
      };
    } else if (legacyProfileVal) {
      chosen = addFromLegacy(platform, legacyProfileVal);
    } else if (legacyUserVal) {
      chosen = addFromLegacy(platform, legacyUserVal);
    }

    if (chosen && !seen.has(chosen.platform)) {
      seen.add(chosen.platform);
      result.push(chosen);
    }
  }

  // Modern `socials` array is stored on `users` (and mirrored on `influencers` for public read).
  // The loop above only covers socialAccounts / socialLinks object / legacy handles — merge array-based socials last.
  const mergeCanonicalFromDoc = (docLike: any) => {
    if (!docLike || typeof docLike !== 'object') return;
    const extras = getCanonicalSocials(docLike);
    for (const social of extras) {
      const platform = social.platform;
      if (!DISPLAY_PLATFORMS.includes(platform) || seen.has(platform)) continue;
      const canonical = ensureCanonicalDisplay(platform, social.handle, social.url);
      if (!canonical?.url) continue;
      seen.add(platform);
      result.push({
        platform,
        handle: canonical.handle,
        url: canonical.url,
        isPrimary: !!social.isPrimary,
        source: social.source === 'onboarding' ? 'onboarding' : 'user',
      });
    }
  };

  mergeCanonicalFromDoc(profile);
  mergeCanonicalFromDoc(userDoc);

  return result;
};

export const getPrimarySocial = (socials: SocialAccount[]) => {
  if (!Array.isArray(socials) || socials.length === 0) return null;
  return socials.find((social) => social.isPrimary) || socials[0];
};

export const getConnectedPlatforms = (socials: SocialAccount[]) => {
  return (socials || []).map((social) => social.platform);
};

export const getConnectedPlatformsFromUserDoc = (userDoc: any): SocialPlatform[] => {
  const ordered = ['tiktok', 'instagram', 'youtube'];
  if (Array.isArray(userDoc?.socials)) {
    const platforms = userDoc.socials
      .map((social) => normalizePlatform(social?.platform || ''))
      .filter((platform) => platform && ordered.includes(platform));
    return ordered.filter((platform) => platforms.includes(platform));
  }

  const legacy = new Set();
  const hasTikTok = userDoc?.tiktokHandle || userDoc?.tiktokUrl || userDoc?.socialLinks?.tiktok;
  const hasInstagram = userDoc?.instagramHandle || userDoc?.instagramUrl || userDoc?.socialLinks?.instagram;
  const hasYouTube = userDoc?.youtubeHandle || userDoc?.youtubeUrl || userDoc?.socialLinks?.youtube;

  if (hasTikTok) legacy.add('tiktok');
  if (hasInstagram) legacy.add('instagram');
  if (hasYouTube) legacy.add('youtube');

  return ordered.filter((platform) => legacy.has(platform));
};

export const formatPlatformLabel = (platform: SocialPlatform) => {
  if (platform === 'tiktok') return 'TikTok';
  if (platform === 'instagram') return 'Instagram';
  if (platform === 'youtube') return 'YouTube';
  return platform;
};

export const upsertSocial = (socials: SocialAccount[], social: SocialAccount) => {
  const existing = socials || [];
  const next = existing.filter((item) => item.platform !== social.platform);
  return [...next, social];
};

export const setPrimarySocial = (socials: SocialAccount[], platform: SocialPlatform) => {
  return (socials || []).map((social) => ({
    ...social,
    isPrimary: social.platform === platform,
  }));
};

export const upsertPrimarySocial = (socials: SocialAccount[], social: SocialAccount) => {
  const next = upsertSocial(socials, { ...social, isPrimary: true });
  return setPrimarySocial(next, social.platform);
};

export const removeSocial = (socials: SocialAccount[], platform: SocialPlatform) => {
  return (socials || []).filter((social) => social.platform !== platform);
};
